// Test Execution Types

export interface Execution {
  id: number
  build_id: number
  tcversion_id: number
  testplan_id: number
  platform_id?: number
  status: ExecutionStatus
  tester_id?: number
  execution_ts: string
  execution_type: ExecutionType
  execution_duration?: number
  notes?: string
  created_at: string
  // Extended fields from joins
  testcase_name?: string
  build_name?: string
  testplan_name?: string
  platform_name?: string
  tester_name?: string
}

export interface ExecutionStep {
  id: number
  execution_id: number
  tcstep_id: number
  status?: ExecutionStatus
  notes?: string
  // Extended fields
  step_number?: number
  actions?: string
  expected_results?: string
}

export interface BuildResults {
  build_id: number
  build_name: string
  testplan_id: number
  testplan_name: string
  total_tests: number
  passed: number
  failed: number
  blocked: number
  not_run: number
  pass_rate: number
  executions: Execution[]
}

export interface TestPlanResults {
  testplan_id: number
  testplan_name: string
  builds: Array<{
    build_id: number
    build_name: string
    total_tests: number
    passed: number
    failed: number
    blocked: number
    not_run: number
    pass_rate: number
  }>
}

// Request/Response Types
export interface CreateExecutionRequest {
  build_id: number
  tcversion_id: number
  testplan_id: number
  platform_id?: number
  status: ExecutionStatus
  notes?: string
  duration?: number
  execution_type?: ExecutionType
}

export interface UpdateExecutionRequest {
  status?: ExecutionStatus
  notes?: string
  duration?: number
}

export interface CreateExecutionStepRequest {
  tcstep_id: number
  status?: ExecutionStatus
  notes?: string
}

export interface ExecutionsResponse {
  executions: Execution[]
  total: number
  limit: number
  offset: number
}

export interface ExecutionStepsResponse {
  steps: ExecutionStep[]
}

// Enums
export enum ExecutionStatus {
  Pending = 'pending',
  Running = 'running',
  Passed = 'passed',
  Failed = 'failed',
  Blocked = 'blocked',
  NotRun = 'not_run',
  Cancelled = 'cancelled',
  Error = 'error',
}

export enum ExecutionType {
  Manual = 1,
  Automated = 2,
}

// Test Run Types (aggregated view)
export interface TestRun {
  id: number
  testplan_id: number
  build_id: number
  test_run_name: string
  status: string
  started_at: string
  completed_at?: string
  total_tests: number
  tests_executed: number
  tests_passed: number
  tests_failed: number
  tests_blocked: number
  tests_cancelled: number
  started_by: number
  execution_mode: string
  tenant_id: string
  organization_id: string
  created_at: string
  updated_at: string
  // Calculated metrics
  pass_rate: number
  execution_progress: number
  duration_seconds?: number
  // Names from joins
  testplan_name: string
  build_name: string
  tester_name: string
}

// Helper functions
export function getExecutionStatusLabel(status: ExecutionStatus | string): string {
  switch (status) {
    case ExecutionStatus.Pending:
      return 'Pending'
    case ExecutionStatus.Running:
      return 'Running'
    case ExecutionStatus.Passed:
      return 'Passed'
    case ExecutionStatus.Failed:
      return 'Failed'
    case ExecutionStatus.Blocked:
      return 'Blocked'
    case ExecutionStatus.NotRun:
      return 'Not Run'
    case ExecutionStatus.Cancelled:
      return 'Cancelled'
    case ExecutionStatus.Error:
      return 'Error'
    default:
      return 'Unknown'
  }
}

export function getExecutionStatusColor(status: ExecutionStatus | string): string {
  switch (status) {
    case ExecutionStatus.Pending:
      return '#9CA3AF' // Gray
    case ExecutionStatus.Running:
      return '#3B82F6' // Blue
    case ExecutionStatus.Passed:
      return '#10B981' // Green
    case ExecutionStatus.Failed:
      return '#EF4444' // Red
    case ExecutionStatus.Blocked:
      return '#F59E0B' // Amber
    case ExecutionStatus.NotRun:
      return '#64748B' // Slate
    case ExecutionStatus.Cancelled:
      return '#F59E0B' // Yellow/Amber
    case ExecutionStatus.Error:
      return '#DC2626' // Dark Red
    default:
      return '#9CA3AF' // Gray
  }
}
